//#region [IMPORT]

import React, { createContext, useEffect, useState, useRef, useContext } from 'react';

// === STYLE / MATERIAL UI ===
import { useTheme } from '@mui/material/styles';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { Box, Card, CardHeader, CardContent, IconButton } from '@mui/material';
import { isFocusedDocument, onDocument, onDocumentFocusId, removeListener, requestDocument, returnFieldFocus } from 'api/prokey/prokey.websocket';

// === COMPONENTS ===
import { Spacer } from 'components/Spacer';
import { Block  } from '../Block';

const BlockMemo = React.memo( Block );

//#endregion

//-----------------------------------------------------------------
function Document( props ) {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const { id, parent, timestamp } = props;

  const theme = useTheme();
  const ref   = useRef();
  
  const test = isFocusedDocument( id );

  const ref_open_status = useRef( {
    was_changed: false,
    was_manual: false,
  } );

  const [is_open , setIsOpen ] = useState( test );
  const [doc, setDoc] = useState({});

  const has_doc = !!doc.Fields;
  const is_visible = doc.is_visible;

  const expand_document_icon = is_open ? <ExpandLess /> : <ExpandMore />;
  
  const resize_observer = new ResizeObserver( debounce( entries => {
    const { was_changed, was_manual } = ref_open_status.current;
    if( was_manual ) {
      ref_open_status.current.was_manual = false;
      return;
    }
    if( was_changed ) {
      ref_open_status.current.was_changed = false;
      returnFieldFocus();
    }
    
  }, 100));

  //#endregion

  //#region [STYLES]

  const _styles = {
    container: {
      display  : is_visible ? '' : 'none',
      variant  : 'primary',
      borderRadius: '0.5rem',
      width: '100%',
      margin: 0,
      marginBottom: '1rem',
      backgroundColor: theme.palette.background.secondary[theme.palette.mode],
    },
    header: {
      display: 'flex',
      height: '4rem',

      alignItems: 'center',

      background: theme.palette.primary.main,
      color     : theme.palette.primary.contrastText,

      fontSize   : doc?.is_final ? '18px' : '16px'   ,
      fontWeight : doc?.is_final ? 'bold' : ''       ,
      fontStyle  : doc?.is_final ? ''     : 'italic' ,

      // === Border
      boxShadow: '-0.5em 0  0.01em' + theme.palette.primary.main,
      

      zIndex: 5,
    },
    page_number:{
      paddingRight: '1rem',
      justifyContent: 'center',
      alignItems: 'center',
      fontSize : '17px',
    },
    content: {
      display: is_open ? '' : 'none',
      margin: 0,
      padding: '1rem',
      height: 'calc( 100% - 4rem )',
      width: '100%',
      overflowY: 'auto',
    },
    blocks: {
      height: '100%',
      width: '100%',
      overflowY: 'auto',
    },
    expand_icon: {
      display: 'flex',
      alignItems: 'center',
    }
  }

  //#endregion

  //#region [EFFECT]

  useEffect(() => {

    function handleDocument(data) {
      setDoc(data);
    }

    // Reset document immediately when id changes
    setDoc({});

    // Defensive cleanup before adding listeners (prevents duplicates)
    removeListener('DOCUMENT-' + id, handleDocument);

    onDocument(id, handleDocument);

    requestDocument(id);

    resize_observer.observe( ref.current );

    return () => {
      removeListener('DOCUMENT-' + id, handleDocument);
    };

  }, [id]);

  useEffect(() => {

    function handleDocumentFocus(data) {
      try {
        const open = data ? !!data.open : true;

        setIsOpen( previous => {
          const was_changed = previous !== open;
          if( !was_changed ) return previous;

          ref_open_status.current.was_changed = was_changed;
          ref_open_status.current.was_manual  = false;
          return open;
        });
      } catch (error) {
        console.warn('handleDocumentFocus');
        console.log(error);
      }
    }

    removeListener('DOCUMENT-FOCUS-' + id, handleDocumentFocus);

    onDocumentFocusId(id, handleDocumentFocus);

    if( isFocusedDocument( id ) ) {
      setIsOpen( true );
    }

    return () => {
      removeListener('DOCUMENT-FOCUS-' + id, handleDocumentFocus);
    };

  }, [id]);

  //#endregion

  //#region [EVENTS]

  //-----------------------------------------------------------------
  function handleClick() {
    //-----------------------------------------------------------------
    setIsOpen( !is_open );
    ref_open_status.current.was_changed = true;
    ref_open_status.current.was_manual  = true;
  }

  //#endregion

  //#region [METHOD]
  
  //-----------------------------------------------------------------
  function debounce(fn, delay = 100) {
    //-----------------------------------------------------------------
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), delay);
    }
  }

  //#endregion

  //#region [DISPLAY]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    return has_doc ? (
      <Box ref={ref} key={id} sx={_styles.container} id={'doc-container-' + id } >
        {displayHeader()}
        {displayBlocks()}
      </Box>
    ) : (
      <Box ref={ref} key={id} sx={_styles.container} id={'doc-container-' + id } >
        <CardHeader id={'doc-header-' + id } sx={_styles.header} title='Loading...' />
      </Box>
    );
  }

  //-----------------------------------------------------------------
  function displayFinalIndicator() {
    //-----------------------------------------------------------------
    if( !doc?.is_final ) return null;
    return (
      <div style={{borderLeft : '0.5rem solid orange', width: 0, height: '100%', overflow: 'hidden'}}>final</div>
    )
  }

  //-----------------------------------------------------------------
  function displayHeader() {
    //-----------------------------------------------------------------
    const FinalIndicator = displayFinalIndicator;
    return (
      <div style={_styles.header}>
        <FinalIndicator />
        <Spacer />
        <div>{`${doc.Type} - ${doc.Name}`}</div>
        <Spacer push_right />
        <div style={_styles.expand_icon}>
          <IconButton onClick={handleClick}>{expand_document_icon}</IconButton>
          <div style={_styles.page_number}>{displayPageNumber()}</div>
        </div>
      </div> 
    )
  }

  //-----------------------------------------------------------------
  function displayPageNumber() {
    //-----------------------------------------------------------------
    if( !doc.first_page_index ) return null;

    return <Box sx={_styles.page_number}>Page: {doc.first_page_index}</Box>
  }

  //-----------------------------------------------------------------
  function displayBlocks() {
    //-----------------------------------------------------------------
    if(!is_open) return null;

    const blocks = doc.Blocks.map( id => <BlockMemo key={id} id={id} /> );
    return (
      <Box id={'doc-block-' + id } sx={_styles.content} >
        { blocks }
      </Box>
    );
  }

  //#endregion

  return display();
}

export { Document };
